'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { type BoxProps } from '@ironhack/design-system2/components/box';
import { Button } from '@ironhack/design-system2/components/button';
import { Flex } from '@ironhack/design-system2/components/flex';
import { FormFeedback } from '@ironhack/design-system2/components/form-feedback';
import { Icon } from '@ironhack/design-system2/components/icon';
import { Text } from '@ironhack/design-system2/components/text';
import { ArrowLeft } from '@ironhack/design-system2/icons/arrow-left';
import { useRouter } from 'next/navigation';
import { isEmpty, omit, pick } from 'ramda';
import { type ReactElement, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSessionStorage } from 'react-use';

import { ContactDetailsFields } from '@/app/components/client/contact-details-fields';
import { FormThankYou } from '@/app/components/client/form-thank-you';
import { PhoneField } from '@/app/components/client/phone-field';
import { SelectField } from '@/app/components/client/select-field';
import { FormSubmit } from '@/app/components/server/form-submit';
import { type HeroApplyForm as HeroApplyFormType } from '@/app/components/server/hero/components/hero-apply-form';
import { HeroApplyFormWrapper } from '@/app/components/server/hero/components/hero-apply-form-wrapper';
import { type BasePageParams, getPageUrl } from '@/app/urls/url-mapping';
import { useFeatureToggle } from '@/hooks/use-feature-toggle';
import { useFormRequest } from '@/hooks/use-form-request';
import { useGtmLocalStorage } from '@/hooks/use-gtm-local-storage';
import { useThankYouSession } from '@/hooks/use-thank-you-session';
import { type ApiResult } from '@/lib/api-helpers/endpoint-manager';
import {
  type DatoCampus,
  type DatoCourse,
  type DatoLocale,
} from '@/lib/datocms/types';
import { sendEvent } from '@/lib/gtm';
import { TurnstileFormToken } from '@/lib/turnstile/components';
import { commonKeys, formatCohortDate, regionToCampusMap } from '@/lib/utils';
import { getHeroApplySchema } from '@/lib/utils/form-validations/forms/hero-apply-form';
import { type EmailInput } from '@/lib/utils/form-validations/schemas/email-schema';
import { type NameInputs } from '@/lib/utils/form-validations/schemas/name-schema';
import {
  type PhoneInputs,
  getPhoneDefaults,
} from '@/lib/utils/form-validations/schemas/phone-schema';
import { type GaPageData, type WebCohort } from '@/types';

type FormData = {
  cToken: string;
  campus: string;
  cohort: string;
  email: string;
  format: string;
  phone: PhoneInputs;
  track: string;
} & EmailInput &
  NameInputs;

type Props = {
  applyForm: HeroApplyFormType;
  gaPageData: GaPageData;
  courses: Array<Pick<DatoCourse, 'code' | 'name'>>;
  campuses: Array<Pick<DatoCampus, 'code' | 'name'>>;
  locale: DatoLocale;
  params: BasePageParams;
} & BoxProps;

export const HeroApplyForm = (props: Props): ReactElement => {
  const {
    applyForm,
    courses,
    campuses,
    gaPageData,
    locale,
    params,
    ...boxProps
  } = props;
  const {
    buttonText,
    campusLabel,
    campusPlaceholder,
    cohortLabel,
    cohortPlaceholder,
    courseLabel,
    coursePlaceholder,
    errorMessage,
    formatLabel,
    formatPlaceholder,
    formOpenButtonText,
    legalText,
    nextStepButtonText,
    noCohortsErrorMessage,
    phoneLabel,
    phonePlaceholder,
    step4Title,
    thankYouButton,
    thankYouText,
    thankYouTitle,
    title,
  } = applyForm;
  const { gaCategory } = gaPageData;
  const { region, language } = params;
  const formRequest = useFormRequest();
  const [step, setStep] = useState(1);
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [websiteCampusPage] = useSessionStorage(
    'campus',
    regionToCampusMap[region][0]
  );

  const formSchema = getHeroApplySchema(applyForm);

  const formMethods = useForm<FormData>({
    defaultValues: {
      campus: '',
      cohort: '',
      format: '',
      phone: getPhoneDefaults(region),
      track: '',
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(formSchema),
  });
  const {
    clearErrors,
    formState: { errors, isSubmitting },
    getValues,
    handleSubmit,
    setError,
    setValue,
    trigger,
    watch,
  } = formMethods;
  const [formFeedback, setFormFeedback] = useState<{
    status: 'error';
    message: string;
  } | null>();
  const [cohortOptions, setCohortOptions] = useState<
    Array<{ key: string; text: string }>
  >([]);
  const [isFetchingCohorts, setIsFetchingCohorts] = useState(false);
  const campus = watch('campus');
  const isNextStepButtonDisabled =
    isFetchingCohorts || isEmpty(campus) || cohortOptions.length === 0;

  const handleStep1 = async () => {
    const isValid = await trigger(['track', 'format']);
    if (!isValid) return;

    setStep(2);

    const { format, track } = getValues();
    sendEvent({
      course: track,
      eventAction: 'find your course_ step1',
      eventCategory: gaCategory,
      eventLabel: `${track}::${format}`,
      format,
      pageType: gaCategory,
    });
  };

  const handleStep2 = async () => {
    const isValid = await trigger(['campus', 'cohort']);
    if (!isValid) return;

    setStep(3);
    sendEvent({
      campus,
      eventAction: 'find your course_ step2',
      eventCategory: gaCategory,
      eventLabel: campus,
      pageType: gaCategory,
    });
  };

  const goBackOneStep = () => setStep(step - 1);

  const handleMobileOpen = () =>
    sendEvent({
      eventAction: 'click find course steps',
      eventCategory: gaCategory,
      pageType: gaCategory,
    });

  // Clean the selected cohort and cohort options everytime the campus changes
  useEffect(() => {
    setValue('cohort', '');
    setCohortOptions([]);
  }, [setValue, campus]);

  useEffect(() => {
    clearErrors('campus');
    const { track, format } = getValues();

    const findCohorts = async () => {
      setIsFetchingCohorts(true);
      const response = await fetch('/api/cohorts', {
        body: JSON.stringify({
          campus,
          format,
          language,
          region,
          track,
        }),
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
      });
      const responseBody = (await response.json()) as ApiResult<WebCohort[]>;
      const formattedCohorts = responseBody.result.map((cohort) => {
        const startDateFormatted = formatCohortDate(cohort.start_date, locale);
        const endDateFormatted = formatCohortDate(cohort.end_date, locale);
        return {
          key: cohort.external_ids.salesforce,
          text: `${startDateFormatted} - ${endDateFormatted}`,
        };
      });
      if (formattedCohorts.length === 0) {
        setError('campus', { type: 'custom', message: noCohortsErrorMessage });
      } else {
        setCohortOptions(formattedCohorts);
      }
      setIsFetchingCohorts(false);
    };

    if (track && format && campus) {
      void findCohorts();
    }
  }, [
    campus,
    clearErrors,
    getValues,
    language,
    locale,
    noCohortsErrorMessage,
    region,
    setError,
  ]);
  const { isEnabled } = useFeatureToggle();
  const router = useRouter();
  const [, setGtmLocalStorage] = useGtmLocalStorage();
  const [, setThankYouSession] = useThankYouSession();
  const setCaptchaToken = (token: string) => setValue('cToken', token);

  const onSubmit = async (formData: FormData): Promise<void> => {
    const action = step === 3 ? 'step3' : 'phone';
    sendEvent({
      eventAction: `find your course_ ${action}`,
      eventCategory: gaCategory,
      pageType: gaCategory,
    });

    setFormFeedback(null);
    try {
      const response = await formRequest({
        data: {
          ...omit(['campus', 'cohort', 'format', 'track'], formData),
          courseId: formData.cohort,
          emailOptIn: true,
          language,
          websiteCampusPage,
        },
        formType: 'apply',
      });

      sendEvent({
        campus: formData.campus,
        course: formData.track,
        email: formData.email,
        eventAction: 'send hero apply form',
        eventCategory: gaCategory,
        eventLabel: `${formData.campus}::${formData.track}::${formData.format}`,
        format: formData.format,
        pageType: gaCategory,
      });
      setGtmLocalStorage({
        applicationData: {
          course: formData.track,
          phoneNumber: formData.phone.phoneNumber,
          ...pick(
            ['campus', 'email', 'firstName', 'format', 'lastName'],
            formData
          ),
        },
        countryName: formData.phone.country,
      });

      if (isEnabled('dynamic-thank-you')) {
        await setThankYouSession({ firstName: formData.firstName, response });

        const url = getPageUrl('thankYouDynamic', params);
        if (url) router.push(url);
      } else {
        setStep(5);
      }
    } catch {
      setFormFeedback({ status: 'error', message: errorMessage });
    }
  };

  return (
    <HeroApplyFormWrapper
      buttonText={formOpenButtonText}
      onFormOpen={handleMobileOpen}
      {...boxProps}
    >
      {step < 4 && (
        <>
          <Text mt={[2, null, 1]} textAlign="center" textStyle="2xl">
            {title}
          </Text>
          <Text
            color="brand.secondary"
            mt={1.5}
            textAlign="center"
            textStyle="mBold"
            textTransform="uppercase"
          >
            {applyForm[`step${step as 1 | 2 | 3}`]}
          </Text>
        </>
      )}
      {step < 5 && (
        <FormProvider {...formMethods}>
          {step > 1 && (
            <Flex
              align="center"
              height={['24px', null, '30px']}
              position="absolute"
              top={3}
            >
              <Icon
                as={ArrowLeft}
                boxSize="24px"
                cursor="pointer"
                onClick={goBackOneStep}
              />
            </Flex>
          )}
          <form noValidate onSubmit={handleSubmit(onSubmit)}>
            <Flex alignItems="center" direction="column">
              {step === 1 && (
                <>
                  <SelectField
                    error={errors.track?.message}
                    isRequired
                    label={courseLabel}
                    mt={[9, null, 5]}
                    name="track"
                    options={courses.map((course) => ({
                      key: course.code,
                      text: course.name,
                    }))}
                    placeholder={coursePlaceholder}
                  />
                  <SelectField
                    error={errors.format?.message}
                    isRequired
                    label={formatLabel}
                    mt={2}
                    name="format"
                    options={['ft', 'pt'].map((format) => ({
                      key: format,
                      text: commonKeys[language][format],
                    }))}
                    placeholder={formatPlaceholder}
                  />
                  <Button mt={3} onClick={handleStep1} w="full">
                    {nextStepButtonText}
                  </Button>
                </>
              )}
              {step === 2 && (
                <>
                  <SelectField
                    error={errors.campus?.message as string}
                    isRequired
                    label={campusLabel}
                    mt={[9, null, 5]}
                    name="campus"
                    options={campuses.map(({ code, name }) => ({
                      key: code,
                      text: name,
                    }))}
                    placeholder={campusPlaceholder}
                  />
                  <SelectField
                    error={errors.cohort?.message as string}
                    isDisabled={isFetchingCohorts || cohortOptions.length === 0}
                    isRequired
                    label={cohortLabel}
                    mt={2}
                    name="cohort"
                    options={cohortOptions}
                    placeholder={cohortPlaceholder}
                  />
                  <Button
                    disabled={isNextStepButtonDisabled}
                    isLoading={isFetchingCohorts}
                    mt={3}
                    onClick={handleStep2}
                    w="full"
                  >
                    {nextStepButtonText}
                  </Button>
                </>
              )}
              {step === 3 && (
                <ContactDetailsFields
                  fields={pick(
                    [
                      'completeFields',
                      'emailLabel',
                      'emailPlaceholder',
                      'firstNameLabel',
                      'firstNamePlaceholder',
                      'lastNameLabel',
                      'lastNamePlaceholder',
                      'phoneLabel',
                      'phonePlaceholder',
                    ],
                    applyForm
                  )}
                  isSmall
                  mt={[9, null, 5]}
                  onGoogleClick={() => {
                    setFormFeedback(null);
                    setIsGoogleSubmitting(true);
                  }}
                  onGoogleError={() => {
                    setIsGoogleSubmitting(false);
                    setFormFeedback({
                      status: 'error',
                      message: errorMessage,
                    });
                  }}
                  onGoogleSuccess={async () => {
                    setIsGoogleSubmitting(false);
                    const isValid = await trigger([
                      'email',
                      'firstName',
                      'lastName',
                    ]);
                    if (isValid) setStep(4);
                  }}
                  withPhone
                />
              )}
              {step === 4 && (
                <>
                  <Text
                    mt={[3, null, 7]}
                    textAlign="start"
                    textStyle="lBold"
                    width="full"
                  >
                    {step4Title}
                  </Text>
                  <PhoneField
                    error={
                      (errors.phone?.message ||
                        errors.phone?.phoneNumber?.message) as string
                    }
                    isRequired
                    label={phoneLabel}
                    mt={2}
                    name="phone"
                    placeholder={phonePlaceholder}
                  />
                </>
              )}
              {step > 2 && (
                <>
                  <TurnstileFormToken
                    language={language}
                    onVerify={setCaptchaToken}
                  />
                  <FormSubmit
                    buttonProps={{ width: 'full' }}
                    buttonText={buttonText}
                    isLoading={isSubmitting || isGoogleSubmitting}
                    legalText={legalText}
                  />
                </>
              )}
              {formFeedback?.status && (
                <FormFeedback mt={2} {...formFeedback} />
              )}
            </Flex>
          </form>
        </FormProvider>
      )}
      {step === 5 && (
        <FormThankYou
          button={thankYouButton}
          buttonProps={{ mt: [4, null, 3], w: 'full' }}
          mt={[0, null, 1]}
          text={thankYouText}
          textAlign="center"
          title={thankYouTitle}
        />
      )}
    </HeroApplyFormWrapper>
  );
};
